import {
  BdArrayUtil,
  OverrideProperty,
  OverridePropertyHelper,
} from '@bindoc-projects/util-domain-lib';
import { ShareableType } from '@bindoc-projects/resource-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';

export const BD_VIRTUAL_CLINIC_BENCHMARKING_GROUP_CONDITION_ID =
  'virtualClinicBenchmarkingGroupIds';
export const FilterConditionVirtualClinicBenchmarkingGroupIdsPluginType: FilterConditionPluginSpecification<
  string[],
  FilterConditionVirtualClinicBenchmarkingGroupIdsDto,
  BdVirtualClinicBenchmarkingGroupIdsCondition
> = {
  type: 'virtualBenchmarkingGroupIds',
  category: 'clinic',
  displayName: { de: 'Benchmarking Gruppe' },
  sortingIndex: 8,
  occurrence: 'single',
  fromObject(
    obj: FilterConditionVirtualClinicBenchmarkingGroupIdsDto,
  ): FilterConditionPluginImpl<string[]> {
    return new FilterConditionVirtualClinicBenchmarkingGroupIdsPlugin(
        obj.virtualBenchmarkingGroupIds,
      );
  },
  fromCondition(
    jsonObj: BdVirtualClinicBenchmarkingGroupIdsCondition,
  ): FilterConditionPluginImpl<string[]> {
    return new FilterConditionVirtualClinicBenchmarkingGroupIdsPlugin(
        jsonObj.includes ?? [],
      );
  },
  newInstance(): FilterConditionPluginImpl<string[]> {
    return new FilterConditionVirtualClinicBenchmarkingGroupIdsPlugin([]);
  },
};

export class FilterConditionVirtualClinicBenchmarkingGroupIdsPlugin extends FilterConditionPluginImpl<
  string[],
  FilterConditionVirtualClinicBenchmarkingGroupIdsDto
> {
  public static override Specification: FilterConditionPluginSpecification<
    string[],
    FilterConditionVirtualClinicBenchmarkingGroupIdsDto,
    BdVirtualClinicBenchmarkingGroupIdsCondition
  > = FilterConditionVirtualClinicBenchmarkingGroupIdsPluginType;

  constructor(private virtualBenchmarkingGroupIds: string[]) {
    super(FilterConditionVirtualClinicBenchmarkingGroupIdsPluginType.type);
  }

  clone(): FilterConditionVirtualClinicBenchmarkingGroupIdsPlugin {
    return new FilterConditionVirtualClinicBenchmarkingGroupIdsPlugin([
      ...this.virtualBenchmarkingGroupIds,
    ]);
  }

  override equals(
    other: FilterConditionVirtualClinicBenchmarkingGroupIdsPlugin,
  ): boolean {
    return (
      super.equals(other) &&
      BdArrayUtil.equals(
        this.virtualBenchmarkingGroupIds,
        other.virtualBenchmarkingGroupIds,
      )
    );
  }

  override getChildResources(): {
    [resourceName in ShareableType]?: string[];
  } {
    const resources: { [resourceName in ShareableType]?: string[] } = {};
    resources[ShareableType.VIRTUAL_CLINIC_BENCHMARKING_GROUP] =
      this.virtualBenchmarkingGroupIds;
    return resources;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  override getOverrideProperties(tileId: string, dependencyVirtualClinicId: string): OverrideProperty[] {
    const result: OverrideProperty[] = [];

    if (
      this.virtualBenchmarkingGroupIds &&
      this.virtualBenchmarkingGroupIds.length > 0
    ) {
      this.virtualBenchmarkingGroupIds.forEach((virtualBenchmarkingGroupId) => {
        result.push(
          new OverrideProperty<string>(
            tileId,
            'virtualBenchmarkingGroupId',
            virtualBenchmarkingGroupId,
            virtualBenchmarkingGroupId,
            'possible',
            undefined,
          ),
        );
      });
    }

    return result;
  }

  override overrideClone(
    override: OverrideProperty[],
  ): FilterConditionVirtualClinicBenchmarkingGroupIdsPlugin {
    const ids: string[] = this.virtualBenchmarkingGroupIds
      ? OverridePropertyHelper.overrideProperties(
          this.virtualBenchmarkingGroupIds,
          'virtualBenchmarkingGroupId',
          override,
          (value) => value,
        )
      : [];
    return new FilterConditionVirtualClinicBenchmarkingGroupIdsPlugin(ids);
  }

  override validationToText(): string {
    if (this.virtualBenchmarkingGroupIds.length === 0) {
      return 'Mindestens eine Benchmarking Gruppe auswählen';
    } else {
      return '';
    }
  }

  toCondition(): BdCondition | null {
    return new BdVirtualClinicBenchmarkingGroupIdsCondition(
        this.virtualBenchmarkingGroupIds,
      );
  }

  toJSON(): FilterConditionVirtualClinicBenchmarkingGroupIdsDto | null {
    return {
      type: this.type,
      virtualBenchmarkingGroupIds: this.virtualBenchmarkingGroupIds,
    };
  }

  override toDetailedText(resolvedNames?: Map<string, string>): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const items = this.virtualBenchmarkingGroupIds.map(
      (id) => resolvedNames?.get(id) ?? id,
    );
    return { title, items };
  }

  toText(): string | null {
    return this.virtualBenchmarkingGroupIds.length === 1
      ? 'eine Benchmarking Gruppe'
      : `${this.virtualBenchmarkingGroupIds.length} Benchmarking Gruppen`;
  }

  toValue(): string[] {
    return this.virtualBenchmarkingGroupIds;
  }
}

export class BdVirtualClinicBenchmarkingGroupIdsCondition extends BdContentCondition<string> {
  constructor(includes: string[] = [], excludes: string[] = []) {
    super(
      BD_VIRTUAL_CLINIC_BENCHMARKING_GROUP_CONDITION_ID,
      includes,
      excludes,
    );
  }
}

interface FilterConditionVirtualClinicBenchmarkingGroupIdsDto {
  type: string;
  virtualBenchmarkingGroupIds: string[];
}
